// DataTable — the centrepiece of the admin console.
//
// Before this, all 24 admin sections hand-rolled their own <table>, their own
// loading state, and their own "no results" text. The four async states
// (loading / empty / error / loaded) are built IN here so a section cannot
// forget one — that omission was the single most common gap in the old admin.
//
// Generic over the row type. Sorting, selection, bulk actions, column
// visibility and pagination are all opt-in: a table that passes only `columns`
// and `rows` is still a good table.
//
// RTL: logical properties only (ps-/pe-/text-start/text-end) — CLAUDE.md §5.

import { cn } from '@fajrarabi/ui';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Icon, type IconName } from './Icon';

export type SortDirection = 'asc' | 'desc';

export interface Column<T> {
  /** Stable key. Used for sorting, column visibility and as the React key. */
  key: string;
  header: ReactNode;
  /** Cell renderer. Kept explicit rather than keyed off the row type so a
   *  column can render anything (links, badges, money, actions). */
  render: (row: T) => ReactNode;
  /** Enables the sort control on this column's header. */
  sortable?: boolean;
  /** Right-align numerics; money and counts read far better this way. */
  align?: 'start' | 'end' | 'center';
  /** Hidden by default but offered in the column-visibility menu. */
  hidden?: boolean;
  /** Never offered for hiding (identity columns). */
  alwaysVisible?: boolean;
  width?: string;
  /** Hide below md. Lets dense tables stay usable on a laptop. */
  hideBelow?: 'md' | 'lg';
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
  rowKey: (row: T) => string;

  loading?: boolean;
  /** Any thrown/fetch error. Renders ErrorState with a retry affordance. */
  error?: unknown;
  onRetry?: () => void;

  /** Shown when there are no rows and we are neither loading nor errored. */
  empty?: {
    title: string;
    description?: string;
    action?: ReactNode;
    icon?: IconName;
  };

  sort?: { key: string; direction: SortDirection } | null;
  onSortChange?: (sort: { key: string; direction: SortDirection }) => void;

  /** Row selection + bulk actions. Omit both to disable selection entirely. */
  selectedIds?: readonly string[];
  onSelectionChange?: (ids: string[]) => void;
  bulkActions?: (selectedIds: string[]) => ReactNode;

  onRowClick?: (row: T) => void;
  /** Visually marks a row (e.g. the record open in a drawer). */
  isRowActive?: (row: T) => boolean;

  /** Rendered above the table: search, filters, date range. */
  toolbar?: ReactNode;
  /** Rendered below the table: pagination. */
  footer?: ReactNode;

  /** Skeleton row count while loading. Match the usual page size. */
  skeletonRows?: number;
  caption?: string;
  className?: string;
  /** Enables the column-visibility menu. */
  columnVisibility?: boolean;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  error,
  onRetry,
  empty,
  sort = null,
  onSortChange,
  selectedIds,
  onSelectionChange,
  bulkActions,
  onRowClick,
  isRowActive,
  toolbar,
  footer,
  skeletonRows = 8,
  caption,
  className,
  columnVisibility = false,
}: DataTableProps<T>) {
  const selectable = Boolean(onSelectionChange);
  const selected = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);

  // Hidden columns are local UI state, not URL state — a colleague opening your
  // link should see the data, not inherit your column preferences.
  const [hiddenKeys, setHiddenKeys] = useState<ReadonlySet<string>>(
    () => new Set(columns.filter((c) => c.hidden).map((c) => c.key)),
  );
  const visibleColumns = useMemo(
    () => columns.filter((c) => !hiddenKeys.has(c.key)),
    [columns, hiddenKeys],
  );

  const allIds = useMemo(() => rows.map(rowKey), [rows, rowKey]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = allIds.some((id) => selected.has(id)) && !allSelected;

  function toggleAll() {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : allIds);
  }

  function toggleOne(id: string) {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange([...next]);
  }

  function headerSortState(col: Column<T>): 'none' | SortDirection {
    if (!col.sortable || sort?.key !== col.key) return 'none';
    return sort.direction;
  }

  function handleSort(col: Column<T>) {
    if (!col.sortable || !onSortChange) return;
    const current = headerSortState(col);
    onSortChange({
      key: col.key,
      // First click on a new column sorts descending: for admin tables the
      // interesting rows (newest, largest, most failed) are almost always at
      // the top of a descending sort.
      direction: current === 'desc' ? 'asc' : 'desc',
    });
  }

  const showBulkBar = selectable && selected.size > 0;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {toolbar}

      {showBulkBar && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-100 bg-brand-25 px-4 py-2.5">
          <span className="text-sm font-medium text-brand-700">{selected.size} selected</span>
          <div className="ms-auto flex items-center gap-2">
            {bulkActions?.([...selected])}
            <button
              type="button"
              onClick={() => onSelectionChange?.([])}
              className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-admin-600 hover:bg-white"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-admin-200 bg-white">
        {columnVisibility && (
          <ColumnVisibilityMenu
            columns={columns}
            hiddenKeys={hiddenKeys}
            onChange={setHiddenKeys}
          />
        )}

        {/* Wide tables scroll inside their own container — the page body must
            never scroll horizontally. */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            {caption && <caption className="sr-only">{caption}</caption>}
            <thead>
              <tr className="border-b border-admin-200 bg-admin-50">
                {selectable && (
                  <th scope="col" className="w-10 px-4 py-3">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                      label="Select all rows"
                      disabled={allIds.length === 0}
                    />
                  </th>
                )}
                {visibleColumns.map((col) => {
                  const state = headerSortState(col);
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      style={col.width ? { width: col.width } : undefined}
                      aria-sort={
                        state === 'none' ? undefined : state === 'asc' ? 'ascending' : 'descending'
                      }
                      className={cn(
                        'px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-admin-500',
                        alignClass(col.align),
                        col.hideBelow === 'md' && 'hidden md:table-cell',
                        col.hideBelow === 'lg' && 'hidden lg:table-cell',
                      )}
                    >
                      {col.sortable && onSortChange ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col)}
                          className={cn(
                            'inline-flex items-center gap-1 rounded transition-colors hover:text-admin-800',
                            col.align === 'end' && 'flex-row-reverse',
                          )}
                        >
                          {col.header}
                          <SortGlyph state={state} />
                        </button>
                      ) : (
                        col.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {loading &&
                Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={`skeleton-${i}`} className="border-b border-admin-100 last:border-0">
                    {selectable && (
                      <td className="px-4 py-3">
                        <div className="h-4 w-4 animate-pulse rounded bg-admin-100" />
                      </td>
                    )}
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'px-4 py-3',
                          col.hideBelow === 'md' && 'hidden md:table-cell',
                          col.hideBelow === 'lg' && 'hidden lg:table-cell',
                        )}
                      >
                        <div
                          className="h-4 animate-pulse rounded bg-admin-100"
                          // Varied widths read as data rather than as a grid of
                          // identical bars.
                          style={{ width: `${55 + ((i * 17 + col.key.length * 7) % 40)}%` }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}

              {!loading &&
                rows.map((row) => {
                  const id = rowKey(row);
                  const isSelected = selected.has(id);
                  const active = isRowActive?.(row) ?? false;
                  return (
                    <tr
                      key={id}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={cn(
                        'border-b border-admin-100 transition-colors last:border-0',
                        onRowClick && 'cursor-pointer',
                        isSelected ? 'bg-brand-25' : active ? 'bg-admin-50' : 'hover:bg-admin-50',
                      )}
                    >
                      {selectable && (
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleOne(id)}
                            label={`Select row ${id}`}
                          />
                        </td>
                      )}
                      {visibleColumns.map((col) => (
                        <td
                          key={col.key}
                          className={cn(
                            'px-4 py-3 text-admin-700',
                            alignClass(col.align),
                            col.hideBelow === 'md' && 'hidden md:table-cell',
                            col.hideBelow === 'lg' && 'hidden lg:table-cell',
                          )}
                        >
                          {col.render(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* The three non-loaded states live OUTSIDE the table element so they
            can centre properly and are not constrained by column widths. */}
        {!loading && error != null && (
          <ErrorState error={error} onRetry={onRetry} className="border-t border-admin-100" />
        )}

        {!loading && error == null && rows.length === 0 && (
          <EmptyState
            icon={empty?.icon ?? 'inbox'}
            title={empty?.title ?? 'Nothing here yet'}
            description={empty?.description}
            action={empty?.action}
            className="border-t border-admin-100"
          />
        )}
      </div>

      {footer}
    </div>
  );
}

function alignClass(align: Column<unknown>['align']): string {
  if (align === 'end') return 'text-end tabular-nums';
  if (align === 'center') return 'text-center';
  return 'text-start';
}

function SortGlyph({ state }: { state: 'none' | SortDirection }) {
  return (
    <span
      aria-hidden
      className={cn(
        'text-[10px] leading-none',
        state === 'none' ? 'text-admin-300' : 'text-brand-500',
      )}
    >
      {state === 'asc' ? '▲' : state === 'desc' ? '▼' : '↕'}
    </span>
  );
}

// Native checkbox with an accessible label. `indeterminate` is a DOM property,
// not an attribute, so it must be set imperatively — React has no prop for it.
function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
      className="h-4 w-4 cursor-pointer rounded border-admin-300 text-brand-500 accent-brand-500 disabled:cursor-not-allowed disabled:opacity-40"
    />
  );
}

function ColumnVisibilityMenu<T>({
  columns,
  hiddenKeys,
  onChange,
}: {
  columns: ReadonlyArray<Column<T>>;
  hiddenKeys: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleable = columns.filter((c) => !c.alwaysVisible);
  if (toggleable.length === 0) return null;

  return (
    <div
      ref={wrapRef}
      className="relative flex justify-end border-b border-admin-100 bg-white px-3 py-2"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={menuId}
        className="inline-flex items-center gap-1.5 rounded-lg border border-admin-200 px-2.5 py-1.5 text-xs font-medium text-admin-600 transition-colors hover:bg-admin-50"
      >
        <Icon name="columns" className="h-3.5 w-3.5" />
        Columns
      </button>

      {open && (
        <div
          id={menuId}
          className="absolute end-3 top-full z-20 mt-1 w-56 rounded-xl border border-admin-200 bg-white p-1.5 shadow-lg"
        >
          {toggleable.map((col) => {
            const visible = !hiddenKeys.has(col.key);
            return (
              <label
                key={col.key}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm text-admin-700 hover:bg-admin-50"
              >
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={() => {
                    const next = new Set(hiddenKeys);
                    if (visible) next.add(col.key);
                    else next.delete(col.key);
                    onChange(next);
                  }}
                  className="h-4 w-4 rounded border-admin-300 accent-brand-500"
                />
                <span className="truncate">
                  {typeof col.header === 'string' ? col.header : col.key}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
